import clsx from 'clsx';
import { useState } from 'react';
import { MenuItem } from '@mui/material';
import { useTextFieldStyles } from '../text-field/text-field.styles';
import { useTextStyles } from '../../../theme/text';
import { useDropdownStyles } from './dropdown.styles';
import { TextField, TextFieldProps, TextFieldVariant } from '../text-field/text-field.component';
import { SVGIconComponent } from '../icons/types';
import { decodeImageURL } from '../../../util/image-url';

export interface DropdownProps<T> extends Omit<TextFieldProps, 'textFieldVariant' | 'condensed' | 'startAdornment'> {
  dropdownVariant: TextFieldVariant;
  shrinkLabel?: boolean;
  textFieldDesktopWidth?: string;
  forceShowLabel?: boolean;
  menuItems: DropdownOption<T>[];
  textColorOverride?: string;
  width?: string;
}

export interface DropdownOption<T> {
  label: string;
  imageURL?: string;
  Icon?: SVGIconComponent;
  value: T;
}

export function Dropdown<T extends string | number>({
  dropdownVariant = 'floating',
  textFieldDesktopWidth,
  forceShowLabel,
  placeholder,
  menuItems,
  className,
  textColorOverride,
  width,
  slotProps,
  ...props
}: DropdownProps<T>) {
  const classes = useDropdownStyles({ variant: dropdownVariant });
  const textFieldClasses = useTextFieldStyles({ variant: dropdownVariant });
  const textClasses = useTextStyles();

  const [open, setOpen] = useState(false);

  return (
    <TextField
      select
      focused={open}
      placeholder={placeholder}
      textFieldVariant={dropdownVariant}
      desktopWidth={textFieldDesktopWidth}
      textColorOverride={textColorOverride}
      {...props}
      // A slot the caller passes replaces ours outright.
      slotProps={{
        ...slotProps,
        input: slotProps?.input ?? {
          className: clsx(classes.dropdownIcon, textFieldClasses.textField, classes.select)
        },
        select: slotProps?.select ?? {
          MenuProps: {
            style: textColorOverride ? { color: textColorOverride } : {},
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            }
          },
          open,
          onOpen: () => setOpen(true),
          onClose: () => setOpen(false),
          style: textColorOverride ? { color: textColorOverride } : {}
        }
      }}
    >
      {menuItems.map(({ label, imageURL, value, Icon }) => (
        <MenuItem className={textClasses.dropdownMenu} key={value} value={value} aria-label={label}>
          {Icon ? <Icon className={classes.selectOptionIcon} /> : null}
          {imageURL ? (
            <img src={decodeImageURL(imageURL)} alt={label} style={{ height: 30, marginRight: 10 }} />
          ) : (
            label
          )}
          {forceShowLabel && imageURL ? label : null}
        </MenuItem>
      ))}
    </TextField>
  );
}
